import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

const InquiryForm = ({
  inquiryType = "product",
  productId = null,
  productName = null,
  subject = "",
  testIdPrefix = "inquiry",
  compact = false,
}) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: subject,
    message: productName ? `I am interested in ${productName}. Please share details.` : "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        ...form,
        inquiry_type: inquiryType,
        product_id: productId,
        product_name: productName,
      });
      toast.success("Inquiry sent! We'll get back to you shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: subject,
        message: productName ? `I am interested in ${productName}. Please share details.` : "",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-4"
      data-testid={`${testIdPrefix}-form`}
    >
      <div className={compact ? "grid gap-4" : "grid gap-4 md:grid-cols-2"}>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-warm">
            Full Name*
          </label>
          <input
            data-testid={`${testIdPrefix}-input-name`}
            name="name"
            value={form.name}
            onChange={handleChange}
            className="arel-input w-full rounded-md px-4 py-3 text-sm"
            placeholder="Ram Sharma"
            required
          />
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-warm">
            Phone*
          </label>
          <input
            data-testid={`${testIdPrefix}-input-phone`}
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="arel-input w-full rounded-md px-4 py-3 text-sm"
            placeholder="+977 98XXXXXXXX"
            required
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-warm">
          Email*
        </label>
        <input
          data-testid={`${testIdPrefix}-input-email`}
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          className="arel-input w-full rounded-md px-4 py-3 text-sm"
          placeholder="you@example.com"
          required
        />
      </div>

      {!subject && (
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-warm">
            Subject
          </label>
          <input
            data-testid={`${testIdPrefix}-input-subject`}
            name="subject"
            value={form.subject}
            onChange={handleChange}
            className="arel-input w-full rounded-md px-4 py-3 text-sm"
            placeholder="Product / dealership / support"
          />
        </div>
      )}

      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-muted-warm">
          Message*
        </label>
        <textarea
          data-testid={`${testIdPrefix}-input-message`}
          name="message"
          value={form.message}
          onChange={handleChange}
          rows={5}
          className="arel-input w-full rounded-md px-4 py-3 text-sm"
          placeholder="Tell us what you need..."
          required
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid={`${testIdPrefix}-submit-btn`}
        className="arel-primary-btn inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold disabled:opacity-60"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {loading ? "Sending..." : "Send Inquiry"}
      </button>
    </form>
  );
};

export default InquiryForm;
